/**
 * Sagt ein Fehler etwas über die Sitzung — oder nur über den Server?
 *
 * Bis 17.10.2026 wertete `useAuth` JEDEN Fehler beim Laden des Nutzers als
 * abgelaufene Sitzung (Start-Prüfung und `stopImpersonation`), und der
 * Refresh-Interceptor warf den Nutzer-Cache bei jedem gescheiterten Refresh
 * weg: Ein Backend, das zehn Sekunden stand (502 vom Proxy beim Deploy), meldete
 * jeden ab. Dieselbe Bug-Klasse wurde am 27.07.2026 in survey/support/marketing
 * und am 17.10.2026 in paperball-news behoben (croeck-pixel/paperball-news#134).
 *
 * Die Entscheidung steht hier, ohne React und ohne Browser — damit `node --test`
 * sie prüfen kann (`sitzungsPruefung.test.ts`).
 */

import { ANMELDE_SIGNAL } from '../../app/_seo/seiten';

/**
 * Vorübergehend ist ein Fehler, nach dem die Cookies sehr wahrscheinlich noch
 * gelten: Der Server war nicht erreichbar (axios-Fehler ohne Antwort, etwa
 * während eines Neustarts), hat zu lange gebraucht (408), ist überlastet (429)
 * oder kaputt (5xx — auch die 502 des Proxys, wenn das Backend gerade steht).
 *
 * Alles andere — vor allem 401 nach gescheitertem Refresh, 403 und 400 („Konto
 * deaktiviert“) — ist eine Antwort des Servers auf die Sitzung selbst.
 */
export function istVoruebergehenderFehler(fehler: unknown): boolean {
  if (typeof fehler !== 'object' || fehler === null) return false;
  const kandidat = fehler as { isAxiosError?: unknown; response?: { status?: unknown } };
  if (kandidat.isAxiosError !== true) return false;
  const status = kandidat.response?.status;
  if (typeof status !== 'number') return true;
  return status === 408 || status === 429 || status >= 500;
}

/**
 * Gibt es überhaupt eine Sitzung zu prüfen? Erkannt am Anmelde-Signal
 * (`pb_csrf`, `ANMELDE_SIGNAL`): Es ist das einzige Cookie der Sitzung, das
 * JavaScript lesen kann, lebt so lange wie das Refresh-Cookie, und die
 * Abmeldung löscht es — dieselbe Frage, mit der Server-Seiten und `proxy.ts`
 * Gäste von Angemeldeten trennen.
 *
 * Bis 25.10.2026 fragte jeder Seitenaufruf eines Gasts `/api/auth/me` (401),
 * danach `/api/auth/refresh` (401): zwei Fehler in der Konsole jeder
 * öffentlichen Seite, und für Crawler, die JavaScript ausführen, zwei
 * Anfragen, die nichts liefern.
 */
export function anmeldeSignalGesetzt(cookies: string, name: string = ANMELDE_SIGNAL): boolean {
  return cookies.split(';').some((teil) => {
    const [schluessel, ...wert] = teil.trim().split('=');
    return schluessel === name && wert.join('=') !== '';
  });
}

export type Sitzungsstand<U> =
  /** Der Server hat die Sitzung bestätigt und den Nutzer geliefert. */
  | { art: 'bestaetigt'; nutzer: U }
  /** Der Server hat die Sitzung abgelehnt — abmelden. */
  | { art: 'beendet'; fehler: unknown }
  /** Der Server hat nicht geantwortet — angemeldet bleiben, Fehler zeigen. */
  | { art: 'unerreichbar'; fehler: unknown };

/** Fragt den Server nach dem Nutzer und ordnet einen Fehler ein. */
export async function pruefeSitzung<U>(holeNutzer: () => Promise<{ data: U }>): Promise<Sitzungsstand<U>> {
  try {
    const antwort = await holeNutzer();
    return { art: 'bestaetigt', nutzer: antwort.data };
  } catch (fehler) {
    return istVoruebergehenderFehler(fehler)
      ? { art: 'unerreichbar', fehler }
      : { art: 'beendet', fehler };
  }
}

interface ImpersonationSchritte<S, U> {
  /** Startet die Impersonation; das Backend setzt die Cookies des Zielkontos. */
  starte: () => Promise<{ data: S }>;
  /** Lädt den Nutzer aus den (jetzt fremden) Cookies. */
  holeNutzer: () => Promise<{ data: U }>;
  /** Beendet die Impersonation; das Backend setzt wieder Admin-Cookies. */
  beende: () => Promise<unknown>;
  /**
   * Der Rückbau ist gescheitert: Die Cookies gehören weiter dem Zielkonto.
   * Der Aufrufer muss das sichtbar machen (Impersonations-Leiste mit „Beenden“).
   */
  bleibtOffen: (start: S) => void;
}

/**
 * Startet eine Impersonation — oder baut sie zurück, wenn das Zielkonto nicht
 * geladen werden kann.
 *
 * Ohne Rückbau lägen die Cookies des Zielkontos aktiv, während die Oberfläche
 * noch das Admin-Konto zeigt; beim nächsten Laden stünde man unangekündigt im
 * fremden Konto. Der ursprüngliche Fehler geht in jedem Fall an den Aufrufer,
 * damit der ihn anzeigt (`extractErrorDetail` liest dessen Antwort).
 */
export async function impersonationStarten<S, U>(schritte: ImpersonationSchritte<S, U>): Promise<{ start: S; nutzer: U }> {
  const { data: start } = await schritte.starte();
  try {
    const { data: nutzer } = await schritte.holeNutzer();
    return { start, nutzer };
  } catch (fehler) {
    try {
      await schritte.beende();
    } catch (rueckbauFehler) {
      console.error('[Auth] Impersonation nicht zurückgebaut — Cookies gehören weiter dem Zielkonto', rueckbauFehler);
      schritte.bleibtOffen(start);
    }
    throw fehler;
  }
}

/**
 * Beendet eine Impersonation beim Server.
 *
 * Ist der Server gerade nicht erreichbar, gilt die Impersonation weiter — die
 * Cookies gehören noch dem Zielkonto. Der Fehler geht dann an den Aufrufer,
 * damit die Impersonations-Leiste stehen bleibt und niemand im fremden Konto
 * auf eine Admin-Seite springt. Lehnt der Server das Ende dagegen ab (etwa 400,
 * weil die Sitzung längst abgelaufen ist), wird lokal aufgeräumt wie bisher.
 */
export async function impersonationBeenden(beende: () => Promise<unknown>): Promise<void> {
  try {
    await beende();
  } catch (fehler) {
    if (istVoruebergehenderFehler(fehler)) throw fehler;
    console.warn('[Auth] Impersonation-Ende vom Server abgelehnt — lokal aufgeräumt', fehler);
  }
}
